import React from 'react';
import { Box, Typography } from '@mui/material';
import { mapRectToContainDisplay } from '../../utils/videoOverlayCoords';
import { getBboxBorderColor } from '../../utils/bboxOverlayColors';
import {
  DEFAULT_RISK_BBOX_COLOR,
  hexToRgba,
  resolveRiskAppearance,
} from '../../constants/riskEventAppearance';

export type OverlayTrack = {
  trackId: number;
  displayBbox: number[];
  class_name: string;
  score: number;
  frame_width?: number;
  frame_height?: number;
  alpha: number;
  motionRisk?: boolean;
  riskLevel?: string;
  riskSubtype?: string;
  riskSubtypeLabel?: string;
  borderColor?: string;
  wearableId?: string;
  wearableConfidence?: number;
  /** OpenPose 이상 자세 등 (bbox 라벨 옆) */
  poseLabel?: string;
};

const RISK_BORDER = DEFAULT_RISK_BBOX_COLOR;
const RISK_FILL = hexToRgba(DEFAULT_RISK_BBOX_COLOR, 0.18);

type Props = {
  cctvId: number;
  riskZones?: number[][];
  /** 특별관리구역 (빨간 박스) */
  specialZones?: number[][];
  tracks: OverlayTrack[];
  containerEl?: HTMLDivElement | null;
  videoEl?: HTMLVideoElement | null;
  layoutTick: number;
  detectionDebug?: { raw: number; shown: number; pipelineMode?: string };
};

/** 서버 bbox(1280×720 등) → 16:9 타일 안 % 좌표 (object-fit contain, 셀=16:9 가정) */
function bboxToPercent(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  sourceW: number,
  sourceH: number
): { left: string; top: string; width: string; height: string } {
  const sw = Math.max(1, sourceW);
  const sh = Math.max(1, sourceH);
  const loX = Math.min(x1, x2);
  const hiX = Math.max(x1, x2);
  const loY = Math.min(y1, y2);
  const hiY = Math.max(y1, y2);
  return {
    left: `${(loX / sw) * 100}%`,
    top: `${(loY / sh) * 100}%`,
    width: `${((hiX - loX) / sw) * 100}%`,
    height: `${((hiY - loY) / sh) * 100}%`,
  };
}

const CentralMonitoringOverlays: React.FC<Props> = ({
  cctvId,
  riskZones,
  specialZones,
  tracks,
  containerEl,
  videoEl,
  layoutTick,
  detectionDebug,
}) => {
  const defaultFw = Math.max(
    1,
    tracks[0]?.frame_width || videoEl?.videoWidth || 1280
  );
  const defaultFh = Math.max(
    1,
    tracks[0]?.frame_height || videoEl?.videoHeight || 720
  );
  void layoutTick;

  const usePx =
    containerEl != null &&
    (containerEl.clientWidth || 0) > 0 &&
    (containerEl.clientHeight || 0) > 0;
  const displayWidth = usePx ? Math.max(1, containerEl!.clientWidth) : 1;
  const displayHeight = usePx ? Math.max(1, containerEl!.clientHeight) : 1;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {(riskZones || []).map((zone, zi) => {
        if (!Array.isArray(zone) || zone.length < 4) return null;
        const [zx1, zy1, zx2, zy2] = zone;
        const zoneStyle = usePx
          ? (() => {
              const mapped = mapRectToContainDisplay(
                zx1,
                zy1,
                zx2,
                zy2,
                defaultFw,
                defaultFh,
                displayWidth,
                displayHeight
              );
              return {
                left: `${mapped.left}px`,
                top: `${mapped.top}px`,
                width: `${mapped.width}px`,
                height: `${mapped.height}px`,
              };
            })()
          : bboxToPercent(zx1, zy1, zx2, zy2, defaultFw, defaultFh);
        return (
          <Box
            key={`water-zone-${cctvId}-${zi}`}
            sx={{
              position: 'absolute',
              ...zoneStyle,
              border: '1px dashed rgba(0, 200, 255, 0.65)',
              backgroundColor: 'rgba(0, 140, 200, 0.12)',
              zIndex: 5,
            }}
          />
        );
      })}
      {(specialZones || []).map((zone, zi) => {
        if (!Array.isArray(zone) || zone.length < 4) return null;
        const [zx1, zy1, zx2, zy2] = zone;
        const zoneStyle = usePx
          ? (() => {
              const mapped = mapRectToContainDisplay(
                zx1,
                zy1,
                zx2,
                zy2,
                defaultFw,
                defaultFh,
                displayWidth,
                displayHeight
              );
              return {
                left: `${mapped.left}px`,
                top: `${mapped.top}px`,
                width: `${mapped.width}px`,
                height: `${mapped.height}px`,
              };
            })()
          : bboxToPercent(zx1, zy1, zx2, zy2, defaultFw, defaultFh);
        return (
          <Box
            key={`special-zone-${cctvId}-${zi}`}
            sx={{
              position: 'absolute',
              ...zoneStyle,
              border: '2px solid rgba(255, 23, 68, 0.85)',
              backgroundColor: 'rgba(255, 23, 68, 0.16)',
              zIndex: 6,
            }}
          />
        );
      })}
      {tracks.map((track, idx) => {
        let [x1, y1, x2, y2] = track.displayBbox;
        let sourceW = Math.max(1, track.frame_width || defaultFw);
        let sourceH = Math.max(1, track.frame_height || defaultFh);
        const vidW = Math.max(0, videoEl?.videoWidth || 0);
        const vidH = Math.max(0, videoEl?.videoHeight || 0);
        // bbox 가 이미 source(스냅샷) 좌표인지 확인. 원본 크기로 스케일된 bbox 에
        // videoWidth 비율을 또 곱하면 박스가 어긋남.
        const maxX = Math.max(Math.abs(x1), Math.abs(x2));
        const maxY = Math.max(Math.abs(y1), Math.abs(y2));
        const inSourceSpace =
          maxX <= sourceW * 1.08 + 2 && maxY <= sourceH * 1.08 + 2;
        if (
          inSourceSpace &&
          vidW > 0 &&
          vidH > 0 &&
          (vidW !== sourceW || vidH !== sourceH)
        ) {
          const sx = vidW / sourceW;
          const sy = vidH / sourceH;
          x1 *= sx;
          x2 *= sx;
          y1 *= sy;
          y2 *= sy;
          sourceW = vidW;
          sourceH = vidH;
        }
        const boxStyle = usePx
          ? (() => {
              const mapped = mapRectToContainDisplay(
                x1,
                y1,
                x2,
                y2,
                sourceW,
                sourceH,
                displayWidth,
                displayHeight
              );
              const clampedLeft = Math.max(
                0,
                Math.min(displayWidth - 1, mapped.left)
              );
              const clampedTop = Math.max(
                0,
                Math.min(displayHeight - 1, mapped.top)
              );
              return {
                left: `${clampedLeft}px`,
                top: `${clampedTop}px`,
                width: `${Math.max(2, Math.min(displayWidth - clampedLeft, mapped.width))}px`,
                height: `${Math.max(2, Math.min(displayHeight - clampedTop, mapped.height))}px`,
              };
            })()
          : bboxToPercent(x1, y1, x2, y2, sourceW, sourceH);
        const atRisk = Boolean(track.motionRisk);
        const riskAppearance = atRisk
          ? resolveRiskAppearance(track.riskSubtype, {
              label: track.riskSubtypeLabel,
              color: track.borderColor,
            })
          : null;
        const borderColor = atRisk
          ? riskAppearance?.color || RISK_BORDER
          : getBboxBorderColor(track.class_name);
        const boxAlpha = typeof track.alpha === 'number' ? track.alpha : 1;
        const riskLevel = (track.riskLevel || '주의').trim();
        const subtypeLabel =
          (track.riskSubtypeLabel || '').trim() || riskAppearance?.label || '';
        return (
          <Box
            key={`track-${track.trackId ?? idx}`}
            sx={{
              position: 'absolute',
              boxSizing: 'border-box',
              ...boxStyle,
              border: atRisk ? `3px solid ${borderColor}` : `2px solid ${borderColor}`,
              backgroundColor: atRisk ? hexToRgba(borderColor, 0.18) : 'transparent',
              boxShadow: atRisk ? `0 0 10px ${borderColor}` : 'none',
              opacity: boxAlpha,
              zIndex: atRisk ? 11 : 10,
            }}
          >
            <Typography
              sx={{
                position: 'absolute',
                top: -22,
                left: 0,
                color: borderColor,
                fontSize: 12,
                fontWeight: atRisk ? 700 : 400,
                background: atRisk ? 'rgba(80,0,20,0.85)' : 'rgba(0,0,0,0.5)',
                px: 0.5,
                borderRadius: 1,
                whiteSpace: 'nowrap',
              }}
            >
            {atRisk ? `위험 · ${subtypeLabel || '위험'} · ${riskLevel} · ` : ''}
              {track.poseLabel ? `${track.poseLabel} · ` : ''}
              #{track.trackId}
              {track.wearableId ? ` · WB:${track.wearableId}` : ''}
              {' · '}
              {track.class_name} {Math.round(Number(track.score ?? 0) * 100)}%
              {track.wearableConfidence != null && track.wearableConfidence > 0
                ? ` · 매칭 ${Math.round(track.wearableConfidence * 100)}%`
                : ''}
            </Typography>
          </Box>
        );
      })}
      <Typography
        sx={{
          position: 'absolute',
          left: 4,
          bottom: 4,
          fontSize: 11,
          color: '#fff',
          background:
            detectionDebug?.pipelineMode === 'raw_person'
              ? 'rgba(180,90,0,0.88)'
              : 'rgba(0,0,0,0.55)',
          px: 0.5,
          borderRadius: 0.5,
          zIndex: 12,
          fontWeight: detectionDebug?.pipelineMode === 'raw_person' ? 700 : 400,
        }}
      >
        {detectionDebug?.pipelineMode === 'raw_person' ? '원본YOLO' : null}
      </Typography>
      <Typography
        sx={{
          position: 'absolute',
          right: 4,
          bottom: 4,
          fontSize: 11,
          color: '#fff',
          background: 'rgba(0,0,0,0.75)',
          px: 0.5,
          borderRadius: 0.5,
          zIndex: 12,
        }}
      >
        {detectionDebug?.raw === -2
          ? '감시OFF'
          : detectionDebug?.raw === -1
            ? 'snap없음'
            : `det:${detectionDebug?.raw ?? 0} / draw:${detectionDebug?.shown ?? tracks.length}`}
        {tracks[0]?.frame_width
          ? ` · ${tracks[0].frame_width}×${tracks[0].frame_height}`
          : ''}
      </Typography>
    </Box>
  );
};

export default CentralMonitoringOverlays;
